import React from 'react';
import css from './css/Element.scss';
import commonCss from './css/SurveyStyle.scss';
import classnames from 'classnames';
import Icon from 'SF/MaterialIcon';
import { pressedEnterOrSpace } from 'utils/accessibility';
import { range } from 'lodash';
import { StudentSurveyElement } from 'utils/types';
import PropTypes from 'prop-types';

const Element = ({surveyElement, editable, onEdit, onDelete}) => {
    //inlineOriented determines whether the radio options should be displayed inline or not.
    const getRadioOptions = (questionId, optionTexts, inlineOriented = false) =>
        optionTexts.map((optionText, index) =>
            <div key={`${questionId}-${index}`} className={classnames(css['template-radio-option'], {[css['inline-option']]: inlineOriented})}>
                <input type='radio'
                       aria-label={optionText}
                       className={classnames(css['radio-btn'], {[css['inline-btn']]: inlineOriented})}
                       name={`question${questionId}-options`}
                       disabled />
                <label className={commonCss['instructional-text']}>{optionText}</label>
            </div>
        )

    const getQuestionResponseField = (questionData) => {
        switch (questionData.questionType) {
            case 'EMAIL': case 'NUMBER': case 'PHONE': case 'SHORT_TEXT': {
                return <div className={css['template-input-field']} data-testid={`input-${questionData.questionId}`} />;
            }
            case 'LONG_TEXT': {
                return <div className={css['template-textbox-field']} data-testid={`textbox-${questionData.questionId}`} />;
            }
            case 'FULL_DATE': case 'MONTH_YEAR': {
                return (
                    <div className={css['date-container']} data-testid={`date-${questionData.questionId}`}>
                        <i className='fa fa-calendar' aria-hidden='true' />
                        <div className={css['template-input-field']} />
                    </div>
                );
            }
            case 'RADIO_SELECT': {
                return getRadioOptions(questionData.questionId, questionData.questionOptions.map(option => option.text));
            }
            case 'YES_NO': {
                return getRadioOptions(questionData.questionId, ['Yes', 'No']);
            }
            case 'SCALE': {
                return (
                    <div className={css['template-scale-options']}>
                        {getRadioOptions(questionData.questionId, range(1, 6), true)}
                    </div>
                )
            }
            case 'CHECKBOX_SELECT': {
                return (
                    questionData.questionOptions.map((option, index) =>
                        <div key={`${questionData.questionId}-${index}`} className={css['template-radio-option']}>
                            <input type='checkbox' className={css['radio-btn']} aria-label={option.text} disabled />
                            <label className={commonCss['instructional-text']}>{option.text}</label>
                        </div>)
                )
            }
        }
    }

    const getDisplayElement = (element) => {
        if (element.elementType === 'QUESTION') {
            return (
                <div>
                    <div className={commonCss['field-label']}>
                        {element.templateQuestion.required && <span className={commonCss['required-asterisk']} aria-hidden>*</span>}
                        {element.templateQuestion.questionText}
                    </div>
                    {getQuestionResponseField(element.templateQuestion)}
                </div>
            );
        } else {
            return (
                <div className={(element.elementType === 'HEADER') ? css['header-element'] : commonCss['instructional-text']}>
                    {element.elementText}
                </div>
            );
        }
    }

    const handleKeyDown = (event, actionType, element) => {
        if (pressedEnterOrSpace(event)) {
            event.preventDefault();
            actionType === 'edit' ? onEdit(element) : onDelete(element);
        }
    }

    const displayText = surveyElement.elementType === 'QUESTION' ? surveyElement.templateQuestion.questionText : surveyElement.elementText;

    return (
        <>
            {editable && <Icon className={css['drag-icon']} icon='open_with' aria-hidden />}
            {getDisplayElement(surveyElement)}
            {editable &&
                <div className={css['action-icons']}>
                    <button aria-label={`Edit ${displayText}`} onClick={() => onEdit(surveyElement)}
                            onKeyDown={(e) => handleKeyDown(e, 'edit', surveyElement)}>
                        <Icon icon='edit' aria-hidden/>
                    </button>
                    <button aria-label={`Delete ${displayText}`} onClick={() => onDelete(surveyElement)}
                            onKeyDown={(e) => handleKeyDown(e, 'delete', surveyElement)}>
                    <Icon icon='delete' aria-hidden/>
                    </button>
                </div>
            }
        </>
    );
};

Element.propTypes = {
    surveyElement: StudentSurveyElement.isRequired,
    editable: PropTypes.bool.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

export default Element;
